/**
 * AuroraVariant — vertical aurora gradient sweeps, bold centered slogan.
 * Soft, premium, "Glossier x Calm" lifestyle vibe.
 */
import { motion } from "framer-motion";
import Wordmark from "./Wordmark";
import { COPY } from "./copy";

function Sweep({ from, to, delay, duration, x }) {
  return (
    <motion.div
      className="absolute top-0 bottom-0 w-[40%] blur-3xl opacity-60"
      style={{
        left: x,
        background: `linear-gradient(180deg, ${from} 0%, transparent 50%, ${to} 100%)`,
      }}
      animate={{ y: [0, -80, 60, 0], opacity: [0.4, 0.7, 0.5, 0.4] }}
      transition={{ duration, repeat: Infinity, delay, ease: "easeInOut" }}
    />
  );
}

export default function AuroraVariant({ portrait }) {
  const headlineSize = portrait ? "text-[150px]" : "text-[135px]";

  return (
    <div className="relative w-full h-full overflow-hidden" style={{ background: "#0B0420" }}>
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 30%, #2A0E55 0%, #0B0420 70%)" }} />
      <Sweep from="rgba(168,85,247,0.7)" to="rgba(236,72,153,0.5)" delay={0} duration={9} x="5%" />
      <Sweep from="rgba(99,102,241,0.6)" to="rgba(168,85,247,0.5)" delay={1.5} duration={11} x="30%" />
      <Sweep from="rgba(236,72,153,0.6)" to="rgba(168,85,247,0.5)" delay={3} duration={10} x="55%" />
      <Sweep from="rgba(124,58,237,0.6)" to="rgba(236,72,153,0.4)" delay={2} duration={12} x="78%" />
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22/></filter><rect width=%22120%22 height=%22120%22 filter=%22url(%23n)%22 opacity=%220.6%22/></svg>')",
        }}
      />

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-16">
        <Wordmark
          size={portrait ? "xl" : "lg"}
          glpColor="#FFFFFF"
          floClass="bg-gradient-to-b from-pink-200 to-fuchsia-400 bg-clip-text text-transparent"
          delay={0.2}
        />
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 1.0, ease: "easeOut" }}
          className={`${headlineSize} font-extrabold leading-[0.9] tracking-tight mt-6`}
        >
          <span className="bg-gradient-to-b from-white to-purple-200 bg-clip-text text-transparent">{COPY.headline1}</span>
          <br />
          <span className="bg-gradient-to-b from-pink-200 to-fuchsia-400 bg-clip-text text-transparent">{COPY.headline2}</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 0.7 }}
          className="text-3xl text-purple-100 mt-8 font-light italic"
        >
          {COPY.sub}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.3, duration: 0.8 }}
          className="mt-10 max-w-[860px]"
        >
          <p className="text-xl text-white font-semibold mb-2">{COPY.body1}</p>
          <p className="text-lg text-purple-100/85 mb-2">{COPY.body2}</p>
          <p className="text-base text-purple-200/75">{COPY.body3}</p>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3.2, duration: 0.6 }}
          className="absolute bottom-14 text-sm tracking-[0.3em] uppercase text-pink-200/85"
        >
          {COPY.footer} ·{" "}
          <span className="text-white font-bold border-b border-pink-300/60 pb-0.5">{COPY.url}</span>
        </motion.p>
      </div>
    </div>
  );
}
